import React, { useState, useEffect } from "react";
import classes from "./CashReciept.module.css";
import api, { withCompanyId } from "../../../Components/API/Api";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import crop from "../../../Assets/repoort.png";
import verified from "../../../Assets/verified.svg";

function CashReciept({ isOpen, onClose, onSuccess }) {
  const [rows, setRows] = useState([
    { accountId: "", accountDisplay: "", accountCode: "", amount: "", showDropdown: false },
  ]);

  const [reference, setReference] = useState("");
  const [supplierSearch, setSupplierSearch] = useState("");
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const navigate = useNavigate();
  const [debitSearch, setDebitSearch] = useState("");
  const [showDebitDropdown, setShowDebitDropdown] = useState(false);
  const [transactionDate, setTransactionDate] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [description, setDescription] = useState("");
  const [debitAccount, setDebitAccount] = useState("");
  const [outstandingAmount, setOutstandingAmount] = useState("");
  const [selectedLoanAccount, setSelectedLoanAccount] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [loanOptions, setLoanOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [loanProductOptions, setLoanProductOptions] = useState([]);
  const [loanProductsLoading, setLoanProductsLoading] = useState(false);
  const [selectedLoanProduct, setSelectedLoanProduct] = useState("");
  const [loanProductSearch, setLoanProductSearch] = useState("");
  const [showLoanProductDropdown, setShowLoanProductDropdown] = useState(false);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paymentTypesLoading, setPaymentTypesLoading] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState("");
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [paymentToLoading, setPaymentToLoading] = useState(false);
  const paymentTypeLower = (selectedPaymentType || "").toLowerCase();
  const [creditAccounts, setCreditAccounts] = useState([]);

  const [totalAmount, setTotalAmount] = useState(""); 
  const [receiptTypes, setReceiptTypes] = useState([]);
  const [receiptTypesLoading, setReceiptTypesLoading] = useState(false);
  const [paymentSearch, setPaymentSearch] = useState("");
  const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);
  const [expenseOptions, setExpenseOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [receiptType, setReceiptType] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [selectedPaymentMode, setSelectedPaymentMode] = useState("");
  const [debitAccountsLoading, setDebitAccountsLoading] = useState(false);
  const [supportingDocument, setSupportingDocument] = useState(null);
  const [supportingError, setSupportingError] = useState("");
  const [payableAccounts, setPayableAccounts] = useState([]);
  const [payableAccountsLoading, setPayableAccountsLoading] = useState(false);
  const [pendingInvoicesMap, setPendingInvoicesMap] = useState({});

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) { setSupportingDocument(null); setSupportingError(""); return; }
    if (file.type !== 'application/pdf') { setSupportingError('Only PDF files are allowed.'); setSupportingDocument(null); return; }
    const max = 2 * 1024 * 1024;
    if (file.size > max) { setSupportingError('File must be 2MB or smaller.'); setSupportingDocument(null); return; }
    setSupportingError(""); setSupportingDocument(file);
  };

  // keep handlePrinter as-is
  const handlePrinter = () => {
    const receiptData = {
      reference,
      transactionDate,
      description,
      selectedCurrency,
      selectedPaymentMode,
      debitAccount,
      totalAmount,
      rows,
    };

    navigate("/preview", {
      state: { invoice: receiptData },
    });
  };

  // --- Update outstandingAmount when reference (customer selection) changes ---
  useEffect(() => {
    if (!receiptType?.toLowerCase()?.includes("credit customer")) {
      setOutstandingAmount("");
      return;
    }

    if (!reference) {
      setOutstandingAmount("");
      return;
    }

    const customerObj = customerOptions.find(c => String(c.id) === String(reference));
    const balance = customerObj?.balance ?? 0;
    setOutstandingAmount(String(Number(balance) || 0));
  }, [reference, customerOptions, receiptType]);

  // --- Auto-fill totalAmount when supplier is selected for "payment from payables" ---
  useEffect(() => {
    const type = (selectedPaymentType || "").toLowerCase().trim();
    
    if (!type.includes("payment from payables")) {
      return;
    }

    if (!reference) {
      setTotalAmount("");
      return;
    }

    const invoiceData = pendingInvoicesMap[reference];
    if (invoiceData) {
      setTotalAmount(String(invoiceData.balance || 0));
    } else {
      setTotalAmount("");
    }
  }, [reference, pendingInvoicesMap, selectedPaymentType]);

  // --- Fetch customers (only when receiptType includes credit customer) ---
  useEffect(() => {
    const fetchCustomers = async () => {
      if (!receiptType?.toLowerCase()?.includes("credit customer")) return;

      try {
        setCustomerLoading(true);
        const token = localStorage.getItem("authToken");
        const res = await api.get('/v1/customer/get');

        if (res.data && Array.isArray(res.data.data)) {
          setCustomerOptions(res.data.data);
        } else {
          setCustomerOptions([]);
        }
      } catch (err) {
        console.error("Error fetching customers:", err);
        setCustomerOptions([]);
      } finally {
        setCustomerLoading(false);
      }
    };

    fetchCustomers();
  }, [receiptType]);

  const getMemberDisplayName = (member) => {
    if (!member) return "";
    return (
      member.name ||
      `${member.first_name || ""} ${member.last_name || ""}`.trim() ||
      member.employee_id ||
      "Unknown"
    );
  };

  const getLoanProductDisplayName = (product) => {
    if (!product) return "";
    return product.description || product.name || `Product ${product.id}`;
  };

  // Fetch members and loan products when modal opens (loan product is independent of member)
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const fetchMembers = async () => {
      try {
        setMemberLoading(true);
        const res = await api.get("/v1/employee/paginated");
        if (res.data?.data?.data && Array.isArray(res.data.data.data)) {
          setEmployeeOptions(res.data.data.data);
        } else {
          setEmployeeOptions([]);
        }
      } catch (err) {
        console.error("Error fetching members:", err);
        setEmployeeOptions([]);
      } finally {
        setMemberLoading(false);
      }
    };

    const fetchLoanProducts = async () => {
      try {
        setLoanProductsLoading(true);
        const res = await api.get("/v1/loantype");
        const products = res.data?.data;
        setLoanProductOptions(Array.isArray(products) ? products : []);
      } catch (err) {
        console.error("Error fetching loan products:", err);
        setLoanProductOptions([]);
      } finally {
        setLoanProductsLoading(false);
      }
    };

    fetchMembers();
    fetchLoanProducts();
  }, [isOpen]);

  // Reset member/product only when modal closes — not when member changes
  useEffect(() => {
    if (!isOpen) {
      setReference("");
      setSelectedLoanProduct("");
      setLoanProductSearch("");
      setShowLoanProductDropdown(false);
    }
  }, [isOpen]);

  // --- Fetch payment types ---
  useEffect(() => {
    const fetchPaymentTypes = async () => {
      setPaymentTypesLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        const res = await api.get('/v1/get_type_of_payments', { params: { name: 'payment' } });
        if (res.data && Array.isArray(res.data.data)) {
          setPaymentTypes(res.data.data);
        } else {
          setPaymentTypes([]);
        }
      } catch (err) {
        setPaymentTypes([]);
      } finally {
        setPaymentTypesLoading(false);
      }
    };
    fetchPaymentTypes();
  }, []);

  // Default payment type for loan payments when the field is not shown in the UI
  useEffect(() => {
    if (!paymentTypes.length || selectedPaymentType) return;
    const loanType = paymentTypes.find((t) => {
      const name = (t.name || t.type_name || "").toLowerCase();
      return name.includes("loan") || name.includes("employee");
    });
    if (loanType) {
      setSelectedPaymentType((loanType.name || loanType.type_name).toLowerCase().trim());
    }
  }, [paymentTypes, selectedPaymentType]);

  // --- Fetch suppliers when selectedPaymentType includes supplier or payable ---
  useEffect(() => {
    const fetchSuppliers = async () => {
      const type = (selectedPaymentType || "").toLowerCase().trim();

      // Handle "payment from payables" - fetch from pending purchase invoices endpoint
      if (type.includes("payment from payables")) {
        setSupplierLoading(true);
        try {
          const token = localStorage.getItem("authToken");
          const res = await api.get('/v1/retrieve-pending-purchase-invoices');

          const invoices = res?.data?.data;
          if (Array.isArray(invoices)) {
            const invoiceMap = {};
            const options = [];
            const processedSuppliers = new Map();

            // Process invoices to group by supplier and create display options
            invoices.forEach((invoice) => {
              const invoiceId = String(invoice.id);
              const supplierName = invoice.supplier?.name || `Supplier ${invoice.supplier_id}`;
              const invoiceNumber = invoice.invoice_number;
              const balance = invoice.balance;
              
              // Store invoice data by its ID
              invoiceMap[invoiceId] = {
                id: invoiceId,
                invoiceId,
                supplierName,
                invoiceNumber,
                balance,
                fullInvoice: invoice,
              };

              // Create display option: "Supplier Name (Invoice Number)"
              const displayLabel = `${supplierName} (${invoiceNumber})`;
              options.push({
                id: invoiceId,
                name: displayLabel,
                balance,
              });
            });

            // Log invoice structure for debugging item extraction
            console.log("📦 Pending invoices API response structure:", {
              firstInvoice: invoices[0],
              allInvoices: invoices
            });

            setPendingInvoicesMap(invoiceMap);
            setSupplierOptions(options);
          } else {
            setPendingInvoicesMap({});
            setSupplierOptions([]);
          }
        } catch (err) {
          console.error("Error fetching pending purchase invoices:", err);
          setPendingInvoicesMap({});
          setSupplierOptions([]);
        } finally {
          setSupplierLoading(false);
        }
        return;
      }

      // Original logic for regular supplier selection
      if (!type.includes("supplier") && !type.includes("payable")) {
        setSupplierOptions([]);
        setPendingInvoicesMap({});
        return;
      }

      setSupplierLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        const res = await api.get('/v1/beneficiary');

        const suppliers = res?.data?.data;
        if (Array.isArray(suppliers)) {
          const options = suppliers.map((s) => ({
            id: s.id,
            name: s.supplier_name || s.name || `Supplier ${s.id}`,
          }));
          setSupplierOptions(options);
          setPendingInvoicesMap({});
        } else {
          setSupplierOptions([]);
          setPendingInvoicesMap({});
        }
      } catch (err) {
        console.error("Error fetching suppliers:", err);
        setSupplierOptions([]);
        setPendingInvoicesMap({});
      } finally {
        setSupplierLoading(false);
      }
    };

    fetchSuppliers();
  }, [selectedPaymentType]);

  // --- Fetch Account Names and Codes ---
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await api.get('/v1/get-expense-accounts');
        if (res.data && res.data.status && Array.isArray(res.data.data)) {
          setAccounts(res.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch expense accounts", err);
      }
    };
    fetchAccounts();
  }, []);

  // --- Fetch Payable Accounts when payment type includes "payable" ---
  useEffect(() => {
    const fetchPayableAccounts = async () => {
      const type = (selectedPaymentType || "").toLowerCase().trim();

      if (!type.includes("payable")) {
        setPayableAccounts([]);
        return;
      }

      setPayableAccountsLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        const res = await api.get('/v1/get-payable-accounts');

        if (res.data && Array.isArray(res.data.data)) {
          setPayableAccounts(res.data.data);
        } else {
          setPayableAccounts([]);
        }
      } catch (err) {
        console.error("Error fetching payable accounts:", err);
        setPayableAccounts([]);
      } finally {
        setPayableAccountsLoading(false);
      }
    };

    fetchPayableAccounts();
  }, [selectedPaymentType]);

  // --- Fetch Payment Methods ---
  useEffect(() => {
    const fetchPaymentModes = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await api.get('/v1/income/get-payment-method');

        if (res.data && Array.isArray(res.data.data)) {
          setPaymentModes(res.data.data);
        } else {
          setPaymentModes([]);
        }
      } catch (err) {
        console.error("Error fetching payment methods:", err);
        setPaymentModes([]);
      }
    };
    fetchPaymentModes();
  }, []);

  // --- Fetch Currencies ---
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await api.get('/v1/fetch-all-currencies');

        if (res.data && Array.isArray(res.data.data)) {
          setCurrencyOptions(res.data.data);

          // ✅ Set NGN as default
          const ngnCurrency = res.data.data.find(c => c.code === "NGN");
          if (ngnCurrency) setSelectedCurrency(ngnCurrency.id);
        }
      } catch (err) {
        console.error("Error fetching currencies:", err);
      }
    };
    fetchCurrencies();
  }, []);

  useEffect(() => {
    const fetchCreditAccounts = async () => {
      setDebitAccountsLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        const res = await api.get('/v1/bank-accounts');

        if (res.data && Array.isArray(res.data.data)) {
          const creditAccounts = res.data.data.map((acc) => ({
            value: acc.id,
            label: acc.gl_name || acc.name || acc.account_name,
            gl_code: acc.gl_code || acc.code,
            fullAccount: acc,
          }));
          setCreditAccounts(creditAccounts);
        } else {
          setCreditAccounts([]);
        }
      } catch (error) {
        console.error("Error fetching credit accounts:", error);
        setCreditAccounts([]);
      } finally {
        setDebitAccountsLoading(false);
      }
    };

    if (isOpen) fetchCreditAccounts();
  }, [isOpen]);

  if (!isOpen) return null;

  // === TABLE HANDLERS ===
  const addLine = () => {
    setRows((prev) => [
      ...prev,
      { accountId: "", accountDisplay: "", amount: "", showDropdown: false },
    ]);
  };

  const duplicateRow = (index) => {
    setRows((prev) => {
      const newRows = [...prev];
      const emptyRow = {
        accountId: "",
        accountDisplay: "",
        amount: "",
        showDropdown: false,
      };
      newRows.splice(index + 1, 0, emptyRow);
      return newRows;
    });
  };

  const removeRow = (index) => {
    setRows((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleChange = (index, field, value) => {
    setRows((prev) => {
      const newRows = [...prev];
      const row = { ...newRows[index] };

      if (field === "showDropdown") {
        row.showDropdown = value;
      } else if (field === "account") {
        row.accountId = value?.id ?? "";
        row.accountDisplay = `${value?.gl_name ?? value?.name ?? ""}`;
        row.accountCode = value?.gl_code ?? value?.code ?? "";
        row.showDropdown = false;
      } else if (field === "accountDisplay") {
        row.accountDisplay = value;
        row.accountId = "";
        row.accountCode = "";
        row.showDropdown = true;
      } else if (field === "amount") {
        row.amount = value;
      }

      newRows[index] = row;
      return newRows;
    });
  };

  const totalDebit = parseFloat(totalAmount || 0);
  const totalCredit = rows.reduce(
    (sum, r) => sum + (parseFloat(r.amount) || 0),
    0
  );
  const isBalanced = totalDebit === totalCredit;

  // === handleSubmit ===
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("✅ handleSubmit triggered");
    const authToken = localStorage.getItem("authToken");

    // For payment from payables, skip balance check
    const skipBalanceCheck = paymentTypeLower.includes("payment from payables");

    if (!skipBalanceCheck && !isBalanced) {
      Swal.fire({
        icon: "error",
        title: "Unbalanced Entry",
        text: "Total Debit and Total Credit must balance before saving.",
        confirmButtonColor: "#3085d6",
        customClass: { popup: "swal-highest-zindex" },
        didOpen: () => {
          const swalPopup = document.querySelector(".swal2-container");
          if (swalPopup) swalPopup.style.zIndex = "99999";
        },
      });
      return;
    }

    // === Validate required fields ===
    const errors = [];

    if (!reference || reference.trim() === "") {
      errors.push("Member name is required");
    }

    if (!selectedLoanProduct || String(selectedLoanProduct).trim() === "") {
      errors.push("Loan product is required");
    }

    if (!transactionDate || transactionDate.trim() === "") {
      errors.push("Transaction date is required");
    }

    if (!selectedPaymentMode || selectedPaymentMode === "") {
      errors.push("Payment mode is required");
    }

    if (!debitAccount || debitAccount === "") {
      errors.push("Credit account (GL Code) is required");
    }

    if (errors.length > 0) {
      Swal.fire({
        icon: "error",
        title: "Missing Required Fields",
        html: errors.map(err => `<p>${err}</p>`).join(""),
        confirmButtonColor: "#3085d6",
        customClass: { popup: "swal-highest-zindex" },
        didOpen: () => {
          const swalPopup = document.querySelector(".swal2-container");
          if (swalPopup) swalPopup.style.zIndex = "99999";
        },
      });
      return;
    }

    setIsSaving(true);

    try {
      const typeObj = paymentTypes.find(
        t => (t.name || t.type_name)?.toLowerCase().trim() === paymentTypeLower
      );

      let payload = {
        description,
        currency: selectedCurrency,
        payment_mode: selectedPaymentMode,
        transaction_date: transactionDate,
        total_amount: totalCredit,
        account_id: rows.map((row) => row.accountId).filter(Boolean),
        breakdown_amount: rows.map((row) => parseFloat(row.amount || 0)),
        type_of_payment: typeObj?.id || "",
        company_id: localStorage.getItem("company_id") || localStorage.getItem("companyId"),
      };

      if (paymentTypeLower.includes("general") || paymentTypeLower.includes("direct")) {
        payload.particulars = reference;
        // Extract account ID from the debitAccount JSON object (backend expects ID, not GL code)
        let accountId = debitAccount;
        try {
          const accountData = JSON.parse(debitAccount);
          accountId = accountData.id;
        } catch (e) {
          // If not JSON, use debitAccount as is
        }
        payload.gl_code = accountId;
      } else if (paymentTypeLower.includes("payment from payables")) {
        // For payment from payables, use the amount entered in the Total Amount field
        const payingAmount = parseFloat(totalAmount || 0);
        payload.total_amount = payingAmount;
        payload.payment_to = reference;
        
        // Get the bank account ID from Credit Account field (the credit/cash account)
        let bankAccountId = debitAccount;
        try {
          const accountData = JSON.parse(debitAccount);
          bankAccountId = accountData.id;
        } catch (e) {
          // If not JSON, use debitAccount as is
        }
        payload.gl_code = bankAccountId;
        
        // Get the payable account from the first table row (the debit/payable account)
        const payableRow = rows && rows.length > 0 ? rows[0] : null;
        const payableAccountId = payableRow?.accountId || "";
        
        // For "payment from payables", the account_id should be the payable account being paid
        if (payableAccountId) {
          payload.account_id = [payableAccountId];
        } else {
          // Fallback to bank account if no payable account selected
          payload.account_id = [bankAccountId];
        }
        
        payload.breakdown_amount = [payingAmount];
        
        // Extract item_ids from the invoice - For payment from payables, we might not always need items
        const invoiceData = pendingInvoicesMap[reference];
        payload.item_id = [];
        
        // Only try to extract items if the invoice has them
        if (invoiceData && invoiceData.fullInvoice) {
          const invoice = invoiceData.fullInvoice;
          
          // Try multiple possible field names for items
          let items = invoice.items;
          if (!items) items = invoice.line_items;
          if (!items) items = invoice.purchase_order_items;
          if (!items) items = invoice.invoice_items;
          if (!items) items = [];
          
          if (Array.isArray(items) && items.length > 0) {
            items.forEach((item) => {
              let itemId = null;
              
              // Try multiple possible field names for item ID (in order of likelihood)
              // First try direct properties
              if (item?.id && typeof item.id !== 'object') {
                itemId = item.id;
              } else if (item?.item_id) {
                itemId = item.item_id;
              } else if (item?.product_id) {
                itemId = item.product_id;
              } 
              // Then try nested properties
              else if (item?.item?.id) {
                itemId = item.item.id;
              } else if (item?.product?.id) {
                itemId = item.product?.id;
              }
              // Last resort - check for any numeric id-like field
              else if (item?.itemId) {
                itemId = item.itemId;
              }
              
              // Only add if we found a valid, non-empty ID
              if (itemId && String(itemId).trim() !== "" && itemId !== "0") {
                payload.item_id.push(String(itemId));
              }
            });
          }
          
          // Log the structure for debugging
          if (items.length > 0) {
            console.log("📦 Invoice items found - Structure of first item:", {
              firstItem: items[0],
              keys: Object.keys(items[0] || {}),
              totalItems: items.length
            });
          }
        }
        
        // Ensure item_id is always an array, even if empty
        if (!Array.isArray(payload.item_id)) {
          payload.item_id = [];
        }
        
        // Final logging
        console.log("✅ Payment from payables data:", {
          invoice: invoiceData?.id,
          paymentAmount: payingAmount,
          extractedItemIds: payload.item_id,
          itemCount: payload.item_id.length
        });
      } else if (reference && selectedLoanProduct) {
        payload.payment_to = reference;
        payload.loan_type = selectedLoanProduct;
        let accountId = debitAccount;
        try {
          const accountData = JSON.parse(debitAccount);
          accountId = accountData.id;
        } catch (e) {
          // use debitAccount as is
        }
        payload.gl_code = accountId;
      } else if (paymentTypeLower.includes("supplier") || paymentTypeLower.includes("payable")) {
        payload.payment_to = reference;
        // Extract account ID from the debitAccount JSON object (backend expects ID, not GL code)
        let accountId = debitAccount;
        try {
          const accountData = JSON.parse(debitAccount);
          accountId = accountData.id;
        } catch (e) {
          // If not JSON, use debitAccount as is
        }
        payload.gl_code = accountId;
      } else {
        // Extract account ID from the debitAccount JSON object (backend expects ID, not GL code)
        let accountId = debitAccount;
        try {
          const accountData = JSON.parse(debitAccount);
          accountId = accountData.id;
        } catch (e) {
          // If not JSON, use debitAccount as is
        }
        payload.gl_code = accountId;
      }

      console.log("Submitting payload:", payload);

      let response;
      if (supportingDocument) {
        const fd = new FormData();
        fd.append('description', payload.description || '');
        fd.append('currency', String(payload.currency || ''));
        fd.append('payment_mode', String(payload.payment_mode || ''));
        fd.append('transaction_date', payload.transaction_date || '');
        if (payload.gl_code) fd.append('gl_code', String(payload.gl_code));
        fd.append('total_amount', String(payload.total_amount || '0'));
        fd.append('type_of_payment', String(payload.type_of_payment || ''));
        fd.append('company_id', String(payload.company_id || ''));

        // Append account_id[] and breakdown_amount[] individually so backend
        // receives arrays (avoids sending JSON string which PHP won't parse)
        if (Array.isArray(payload.account_id)) {
          (payload.account_id || []).forEach((id) => {
            fd.append('account_id[]', String(id));
          });
        }

        if (Array.isArray(payload.breakdown_amount)) {
          (payload.breakdown_amount || []).forEach((amt) => {
            fd.append('breakdown_amount[]', String(amt || '0'));
          });
        }

        if (payload.particulars) fd.append('particulars', String(payload.particulars));
        if (payload.payment_to) fd.append('payment_to', String(payload.payment_to));
        
        // Append item_id[] for payables payments - only if items were extracted
        if (payload.item_id && Array.isArray(payload.item_id) && payload.item_id.length > 0) {
          payload.item_id.forEach((id) => {
            // Only append non-empty IDs
            const idStr = String(id).trim();
            if (idStr && idStr !== "0" && idStr !== "undefined" && idStr !== "null") {
              fd.append('item_id[]', idStr);
              console.log(`📌 Appending item_id: ${idStr}`);
            }
          });
          console.log(`✅ Total item_id[] values appended: ${payload.item_id.length}`);
        } else {
          console.warn('⚠️  No valid item IDs found for payables payment. This may cause backend validation errors if items are required.');
          // Do NOT append empty item_id[] - let backend handle missing items gracefully
        }
        
        fd.append('supporting_document', supportingDocument, supportingDocument.name);

        response = await api.post('/v1/post-payments', withCompanyId(fd));
      } else {
        // include supporting_document field even when no file uploaded
        payload.supporting_document = supportingDocument ? supportingDocument.name : null;
        response = await api.post('/v1/post-payments', withCompanyId(payload));
      }

      if (response.status === 200 || response.status === 201) {
        Swal.fire({
          imageUrl: verified,
          title: "Payment Saved",
          text: response.data?.message || "The payment has been successfully saved!",
          showDenyButton: true,
          showCancelButton: false,
          confirmButtonText: "Print",
          denyButtonText: "OK",
          confirmButtonColor: "#16A34A",
          denyButtonColor: "#3085d6",
          customClass: { popup: "swal-highest-zindex" },
          didOpen: () => {
            const swalContainer = document.querySelector(".swal2-container");
            if (swalContainer) swalContainer.style.zIndex = "99999";
          },
        }).then((result) => {
          if (result.isConfirmed) {
            const receiptData = {
              reference,
              transactionDate,
              description,
              selectedCurrency,
              selectedPaymentMode,
              debitAccount,
              totalAmount: totalCredit,
              rows,
            };

            navigate("/preview", {
              state: { invoice: receiptData },
            });
          } else if (result.isDenied) {
            // Call onSuccess to refresh the table
            if (onSuccess) onSuccess();
            // Close the modal
            onClose();
          }
        });
      }
    } catch (error) {
      console.error("Save error:", error);
      Swal.fire({
        imageUrl: crop,
        title: "Save Failed",
        text:
          typeof error.response?.data?.message === "object"
            ? Object.values(error.response.data.message).flat().join(", ")
            : error.response?.data?.message ||
            "An error occurred while saving the payment. Please try again.",
        confirmButtonColor: "#000000ff",
        customClass: { popup: "swal-highest-zindex" },
        didOpen: () => {
          const swalContainer = document.querySelector(".swal2-container");
          if (swalContainer) swalContainer.style.zIndex = "99999";
        },
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={classes.modalOverlay}>
      <div className={classes.modalContent}>
        <h3 className={classes.title}>Payment</h3>
        <div className={classes.topRow}>
          <div className={classes.Group3}>
            <label>Member Name</label>
            <select
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              disabled={memberLoading}
            >
              <option value="">Select Member</option>
              {memberLoading ? (
                <option value="" disabled>Loading members...</option>
              ) : (
                employeeOptions.map((member) => (
                  <option key={member.id} value={member.id}>
                    {getMemberDisplayName(member)}
                  </option>
                ))
              )}
            </select>
          </div>
          <div className={classes.Group3}>
            <label htmlFor="loanProductSearch">Loan Product</label>
            <div className={classes.searchableDropdown}>
              <input
                id="loanProductSearch"
                type="text"
                placeholder={loanProductsLoading ? "Loading loan products..." : "Select or search loan product..."}
                value={loanProductSearch}
                onChange={(e) => {
                  setLoanProductSearch(e.target.value);
                  setShowLoanProductDropdown(true);
                  if (!e.target.value.trim()) {
                    setSelectedLoanProduct("");
                  }
                }}
                onFocus={() => setShowLoanProductDropdown(true)}
                onClick={() => setShowLoanProductDropdown(true)}
                className={classes.accountInput}
                disabled={loanProductsLoading}
                autoComplete="off"
              />

              {showLoanProductDropdown && !loanProductsLoading && (
                <div
                  className={classes.dropdownList}
                  onMouseLeave={() => setShowLoanProductDropdown(false)}
                >
                  {loanProductOptions.length === 0 ? (
                    <div className={classes.dropdownLoading}>No loan products found</div>
                  ) : (
                    loanProductOptions
                      .filter((product) => {
                        const q = (loanProductSearch || "").toLowerCase().trim();
                        if (!q) return true;
                        return getLoanProductDisplayName(product).toLowerCase().includes(q);
                      })
                      .slice(0, 30)
                      .map((product) => (
                        <div
                          key={product.id}
                          className={classes.dropdownItem}
                          onClick={() => {
                            setSelectedLoanProduct(String(product.id));
                            setLoanProductSearch(getLoanProductDisplayName(product));
                            setShowLoanProductDropdown(false);
                          }}
                        >
                          {getLoanProductDisplayName(product)}
                        </div>
                      ))
                  )}
                </div>
              )}
            </div>
          </div>
          <div className={classes.Group}>
            <label>Date</label>
            <input
              type="date"
              value={transactionDate}
              onChange={(e) => setTransactionDate(e.target.value)}
              required
            />
          </div>

          <div className={classes.Group}>
            <label>Currency</label>
            <select
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency(e.target.value)}
            >
              <option value="">Select Currency</option>
              {currencyOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code || c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={classes.topRows}>
          <div className={classes.Group1}>
            <label>Description</label>
            <input
              type="text"
              placeholder="Enter description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className={classes.Group2}>
            <label>Mode of Payment</label>
            <div className={classes.searchableDropdown}>
              <input
                type="text"
                placeholder="Search or select payment mode..."
                value={paymentSearch}
                onChange={(e) => setPaymentSearch(e.target.value)}
                onFocus={() => setShowPaymentDropdown(true)}
                onClick={() => setShowPaymentDropdown(!showPaymentDropdown)}
                className={classes.accountInput}
              />

              {showPaymentDropdown && (
                <div
                  className={classes.dropdownList}
                  onMouseLeave={() => setShowPaymentDropdown(false)}
                >
                  {paymentModes.length === 0 ? (
                    <div>No payment modes found</div>
                  ) : (
                    paymentModes
                      .filter((m) =>
                        (m.name || m.method_name || "").toLowerCase().includes(paymentSearch.toLowerCase())
                      )
                      .map((m) => (
                        <div
                          key={m.id}
                          className={classes.dropdownItem}
                          onClick={() => {
                            setSelectedPaymentMode(m.id);
                            setPaymentSearch(m.name || m.method_name || "");
                            setShowPaymentDropdown(false);
                          }}
                        >
                          {m.name || m.method_name}
                        </div>
                      ))
                  )}
                </div>
              )}
            </div>
          </div>

          <div className={classes.Group2}>
            <label>Credit Account</label>
            <div className={classes.searchableDropdown}>
              <input
                type="text"
                placeholder="Search or select debit account..."
                value={debitSearch}
                onChange={(e) => setDebitSearch(e.target.value)}
                onFocus={() => setShowDebitDropdown(true)}
                onClick={() => setShowDebitDropdown(!showDebitDropdown)}
                className={classes.accountInput}
                disabled={debitAccountsLoading}
              />

              {showDebitDropdown && (
                <div
                  className={classes.dropdownList}
                  onMouseLeave={() => setShowDebitDropdown(false)}
                >
                  {debitAccountsLoading ? (
                    <div>Loading accounts...</div>
                  ) : creditAccounts.length === 0 ? (
                    <div>No accounts found</div>
                  ) : (
                    creditAccounts
                      .filter((acc) =>
                        (acc.label || "").toLowerCase().includes(debitSearch.toLowerCase())
                      )
                      .map((acc) => (
                        <div
                          key={acc.value}
                          className={classes.dropdownItem}
                          onClick={() => {
                            setDebitAccount(JSON.stringify({ id: acc.value, gl_code: acc.gl_code }));
                            setDebitSearch(acc.label || "");
                            setShowDebitDropdown(false);
                          }}
                        >
                          {acc.label}
                        </div>
                      ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={classes.top}>
          <div className={classes.Group1}>
            <label>Total Amount</label>
            <input
              type="number"
              placeholder="Enter Amount"
              style={{ textAlign: "right" }}
              value={totalAmount}
              onChange={(e) => setTotalAmount(e.target.value)}
            />
          </div>

          <div className={classes.Group2}>
            <label>Loan Account</label>
            <select
              value={selectedLoanAccount}
              onChange={(e) => setSelectedLoanAccount(e.target.value)}
              disabled
            >
              <option value="">Select Loan Account (Coming Soon)</option>
              <option value="loan-1">Loan #001 - Placeholder</option>
              <option value="loan-2">Loan #002 - Placeholder</option>
            </select>
          </div>

          <div className={classes.Group2}>
            <label>Reference/Income/Loan No.</label>
            <input
              type="text"
              placeholder="Enter reference number"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {(() => {
            const selectedModeName = paymentModes.find(
              (m) => m.id == selectedPaymentMode
            )?.name?.toLowerCase();

            if (selectedModeName?.includes("loan")) {
              return (
                <div className={classes.Group2}>
                  <label>Outstanding</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={outstandingAmount || ""}
                    disabled
                  />
                </div>
              );
            }
            return null;
          })()}
        </div>

        <div className={classes.Group2}>
          <label>Supporting Document</label>
          <input
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            className={classes.fileInput}
          />
          {supportingError && (<div style={{ color: 'red', marginTop: 6 }}>{supportingError}</div>)}
          {supportingDocument && (
            <div style={{ marginTop: 6 }}>
              {supportingDocument.name} <button type="button" onClick={() => { setSupportingDocument(null); setSupportingError(""); }}>Remove</button>
            </div>
          )}
        </div>

        <table className={classes.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Account Name</th>
              <th>Account Code</th>
              <th>Amount</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td className={classes.accountCell}>
                  <div className={classes.searchableDropdown}>
                    <input
                      type="text"
                      value={row.accountDisplay}
                      onChange={(e) => handleChange(index, "accountDisplay", e.target.value)}
                      onFocus={() => handleChange(index, "showDropdown", true)}
                      onClick={() =>
                        handleChange(index, "showDropdown", !row.showDropdown)
                      }
                      placeholder="Search or type account..."
                      className={classes.accountInput}
                    />

                    {row.showDropdown && (
                      <div
                        className={classes.dropdownList}
                        onMouseLeave={() => handleChange(index, "showDropdown", false)}
                      >
                        {paymentTypeLower.includes("payable") ? (
                          payableAccountsLoading ? (
                            <div className={classes.dropdownLoading}>
                              <Spinner animation="border" size="sm" />
                            </div>
                          ) : (
                            payableAccounts
                              .filter((acc) => {
                                const search = row.accountDisplay?.toLowerCase() || "";
                                const text = `${acc.gl_code ?? acc.code ?? ""} ${acc.gl_name ?? acc.name ?? acc.account_name ?? ""
                                  }`.toLowerCase();
                                return text.includes(search);
                              })
                              .map((acc) => (
                                <div
                                  key={acc.id}
                                  className={classes.dropdownItem}
                                  onClick={() => handleChange(index, "account", acc)}
                                >
                                  {`${acc.gl_code ?? acc.code ?? ""} - ${acc.gl_name ?? acc.name ?? acc.account_name ?? ""
                                    }`}
                                </div>
                              ))
                          )
                        ) : (
                          accountsLoading ? (
                            <div className={classes.dropdownLoading}>
                              <Spinner animation="border" size="sm" />
                            </div>
                          ) : (
                            accounts
                              .filter((acc) => {
                                const search = row.accountDisplay?.toLowerCase() || "";
                                const text = `${acc.gl_code ?? acc.code ?? ""} ${acc.gl_name ?? acc.name ?? ""
                                  }`.toLowerCase();
                                return text.includes(search);
                              })
                              .map((acc) => (
                                <div
                                  key={acc.id}
                                  className={classes.dropdownItem}
                                  onClick={() => handleChange(index, "account", acc)}
                                >
                                  {`${acc.gl_code ?? acc.code ?? ""} - ${acc.gl_name ?? acc.name ?? ""
                                    }`}
                                </div>
                              ))
                          )
                        )}
                      </div>
                    )}
                  </div>
                </td>

                <td>
                  <input
                    type="text"
                    value={row.accountCode}
                    readOnly
                    className={classes.readOnlyInput}
                  />
                </td>

                <td>
                  <input
                    type="number"
                    className={classes.creditInput}
                    value={row.amount}
                    onChange={(e) =>
                      handleChange(index, "amount", e.target.value)
                    }
                  />
                </td>

                <td>
                  <button
                    onClick={() => duplicateRow(index)}
                    className={classes.linkBtn}
                    disabled={paymentTypeLower.includes("supplier")}
                    style={{
                      opacity: paymentTypeLower.includes("supplier") ? 0.6 : 1,
                      cursor: paymentTypeLower.includes("supplier") ? "not-allowed" : "pointer",
                    }}
                  >
                    Duplicate
                  </button>
                  <button
                    onClick={() => removeRow(index)}
                    className={classes.removeBtn}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className={classes.actionRow}>
          <button className={classes.addLineBtn} onClick={addLine}>
            + Add Line
          </button>
        </div>

        <div className={classes.totalRow}>
          <div className={classes.totalBox}>
            <span>Total Debit</span>
            <strong>{totalDebit.toFixed(2)}</strong>
          </div>
          <div className={classes.totalBox}>
            <span>Total Credit</span>
            <strong>{totalCredit.toFixed(2)}</strong>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={classes.footerButtons}>
            <button
              type="button"
              className={classes.resetBtn}
              onClick={() =>
                setRows([
                  {
                    accountId: "",
                    accountDisplay: "",
                    amount: "",
                    showDropdown: false,
                  },
                ])
              }
            >
              Reset
            </button>

            <button type="button" className={classes.printBtn} onClick={handlePrinter}>
              Print
            </button>

            <button
              type="submit"
              className={classes.submitBtn}
              disabled={isSaving || !isBalanced || !reference || !selectedLoanProduct || !transactionDate || !selectedPaymentMode || !debitAccount}
            >
              {isSaving ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Spinner animation="border" size="sm" /> Saving...
                </span>
              ) : (
                "Save Payment"
              )}
            </button>
          </div>
        </form>

        <button className={classes.closeBtn} onClick={onClose}>
          ×
        </button>
      </div>
    </div>
  );
}

export default CashReciept;